import { Command } from "cmdk";

import {
  insightsSearchResultAccessibleName,
  type InsightsSearchGroupView,
  type InsightsSearchResult,
} from "../../lib/insights-search.js";

function ResultMeta({ result }: { result: InsightsSearchResult }) {
  const origin = result.metadata?.origin ?? result.metadata?.kind ?? result.description;
  if (!origin) return null;
  return <span className="shrink-0 text-xs text-fg-subtle">{origin}</span>;
}

function ResultBody({ result }: { result: InsightsSearchResult }) {
  const diagnostics = import.meta.env.DEV ? `${result.source} · ${result.rankReason}` : undefined;
  return (
    <>
      <span className="min-w-0 flex-1 truncate">{result.title}</span>
      <ResultMeta result={result} />
      {diagnostics ? (
        <span className="sr-only">
          {diagnostics}
          {result.subjectId ? ` · ${result.subjectId}` : ""}
        </span>
      ) : null}
    </>
  );
}

const itemClass =
  "flex cursor-pointer items-center justify-between gap-2 rounded-soft-md px-2 py-2 text-sm text-fg transition-colors data-[selected=true]:bg-surface-muted";

export function InsightsSearchResults({
  grouped,
  query,
  canAskInsights,
  emptyLabel,
  variant,
  selectedId,
  onSelect,
  onAskInsights,
}: {
  grouped: InsightsSearchGroupView[];
  query: string;
  canAskInsights: boolean;
  emptyLabel: string;
  variant: "page" | "command";
  selectedId?: string;
  onSelect: (result: InsightsSearchResult) => void;
  onAskInsights: () => void;
}) {
  const askLabel = `Ask Insights about “${query.trim()}”`;

  if (variant === "command") {
    return (
      <>
        <Command.Empty className="px-2 py-6 text-center text-sm text-fg-subtle">
          {emptyLabel}
        </Command.Empty>
        {grouped.map((section) => (
          <Command.Group key={section.group} heading={section.heading}>
            {section.items.map((result) => (
              <Command.Item
                key={result.id}
                value={result.id}
                aria-label={insightsSearchResultAccessibleName(result)}
                data-search-type={result.type}
                onSelect={() => onSelect(result)}
                className={itemClass}
              >
                <ResultBody result={result} />
              </Command.Item>
            ))}
          </Command.Group>
        ))}
        {canAskInsights ? (
          <Command.Group heading=" ">
            <Command.Item
              value={`ask-insights:${query}`}
              aria-label={askLabel}
              data-search-type="ask"
              onSelect={onAskInsights}
              className={`${itemClass} mt-1 border-t border-border pt-3 font-medium text-brand`}
            >
              {askLabel}
            </Command.Item>
          </Command.Group>
        ) : null}
      </>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.length === 0 ? (
        <p className="text-sm text-fg-subtle">{emptyLabel}</p>
      ) : (
        grouped.map((section) => (
          <section key={section.group}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-fg-subtle">
              {section.heading}
            </h2>
            <ul className="overflow-hidden rounded-2xl border border-border bg-surface shadow-rest">
              {section.items.map((result) => {
                const selected = result.id === selectedId;
                return (
                  <li key={result.id}>
                    <button
                      type="button"
                      aria-label={insightsSearchResultAccessibleName(result)}
                      data-search-type={result.type}
                      onClick={() => onSelect(result)}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted ${
                        selected ? "bg-surface-muted" : ""
                      }`}
                    >
                      <ResultBody result={result} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      {canAskInsights ? (
        <button
          type="button"
          aria-label={askLabel}
          data-search-type="ask"
          onClick={onAskInsights}
          className="text-sm font-semibold text-brand hover:underline"
        >
          {askLabel}
        </button>
      ) : null}
    </div>
  );
}
